import React, { useEffect, useRef, useState } from 'react';
import ActionableItem from 'components/common/receipt/actionable_item';
import BundleOfferCard from 'components/common/receipt/bundle_offer_card';
import { Box, Divider, Grid } from '@mui/material';
import CDP_EVENT_NAMES from 'constants/cdp_events';
import CartBanner, { CART_BANNER_CONFIGS } from 'components/common/cart_banner';
import { fetchSoftwareSummaries, isKioskSoftware } from 'utilities/software';
import {
  BUNDLE_OFFER_RESOLUTIONS,
  BUNDLE_SQUARE_BASE_PRODUCT_KEYS,
  BUNDLE_SQUARE_TIER_BY_BASE_PRODUCT_KEY,
  SOFTWARE_BASE_PRODUCT_KEYS,
} from 'constants/software';
import { onCartPageClick } from 'components/pages/cart/utilities';
import { useOrderDetails } from 'components/context/order_details';
import {
  BUNDLE_CDP_EVENT_SOURCES,
  trackBundleCdpEvent,
} from 'utilities/bundle_cdp_events';

export const SECTION_TYPES = Object.freeze({
  HARDWARE: 'hardware',
  SOFTWARE: 'software',
});

const ActionableCartSection = (props) => {
  const { trackingData } = useOrderDetails();
  const [disabled, setDisabled] = useState(false);
  const bundleOfferedTrackedRef = useRef(false);

  const {
    orderData,
    sectionTitle,
    cartSection,
    shipQuotes,
    cartClient,
    orderRefresh,
    titleButton,
    emptyComponent,
    sectionType,
    bopisEligible,
    isPnpSubscribed,
    locationCount,
    kioskDeviceCount,
    k1aDeviceCount,
    isReadOnly,
  } = props;

  const handleQtyChange = (variantToken, oldQty, newQty) => {
    const delta = newQty - oldQty;
    if (delta === 0) {
      return;
    }
    setDisabled(true);
    const action = delta > 0 ? 'add' : 'subtract';
    cartClient
      .updateQuantity(variantToken, Math.abs(delta), [], action)
      .then(orderRefresh)
      .then(() => {
        return new Promise((resolve) => {
          const targetProduct = cartSection.find(
            (item) => item.product_variant.token === variantToken,
          );

          onCartPageClick({
            actionItem: 'change_quantity',
            additionalParameters: {
              targetProduct: {
                productKey: targetProduct.product_key,
                variantToken: targetProduct.product_variant.token,
                title: targetProduct.product_variant.title,
              },
              ...trackingData,
              action,
              delta,
            },
          });
          resolve();
        });
      })
      .finally(() => setDisabled(false));
  };

  // The BundleOfferCard "Add" CTA is the only way a Square One bundle line
  // enters the cart — the cart reconciler removes these lines but never adds
  // them. Adds the recommended tier's variant via the same cart client used
  // for quantity changes.
  const handleBundleAdd = (variantToken) => {
    if (!variantToken) return Promise.resolve();
    setDisabled(true);
    return cartClient
      .updateQuantity(variantToken, 1, [], 'add')
      .then(orderRefresh)
      .then(() => {
        trackBundleCdpEvent({
          eventName: CDP_EVENT_NAMES.SAAS_ON_SHOP.BUNDLE_PLAN_ADDED,
          trackingData,
          offer: orderData?.hardware_bundle_offer,
          addedTier: orderData?.hardware_bundle_offer?.recommended_tier,
          variantToken,
          source: BUNDLE_CDP_EVENT_SOURCES.CART_CARD,
        });

        onCartPageClick({
          actionItem: 'add_bundle_offer',
          additionalParameters: {
            ...trackingData,
            recommendedTier: orderData?.hardware_bundle_offer?.recommended_tier,
            variantToken,
          },
        });
      })
      .finally(() => setDisabled(false));
  };

  // Merged carts can carry multiple hidden Square bundle lines; track every match.
  const bundleCartItems =
    sectionType === SECTION_TYPES.SOFTWARE
      ? cartSection.filter((item) =>
          BUNDLE_SQUARE_BASE_PRODUCT_KEYS.includes(item.base_product_key),
        )
      : [];
  const selectedBundleCartItem = bundleCartItems[0];
  const selectedBundleTier =
    BUNDLE_SQUARE_TIER_BY_BASE_PRODUCT_KEY[
      selectedBundleCartItem?.base_product_key
    ];
  const hasExtraBundleCartItems = bundleCartItems.length > 1;

  const handleBundleSelect = (tierKey) => {
    const tier = orderData?.hardware_bundle_offer?.tiers?.[tierKey];
    const nextVariantToken = tier?.variant_token;

    if (
      !nextVariantToken ||
      (tierKey === selectedBundleTier && !hasExtraBundleCartItems) ||
      (selectedBundleCartItem && !selectedBundleCartItem.product_variant?.token)
    ) {
      return Promise.resolve();
    }

    const operation = selectedBundleCartItem ? 'switch' : 'add';
    const bundleSwitchUpdatesByVariantToken = new Map();
    bundleCartItems.forEach((item) => {
      const variantToken = item.product_variant?.token;
      if (!variantToken) return;
      bundleSwitchUpdatesByVariantToken.set(variantToken, {
        variantToken,
        quantity: 0,
        addOnIds: [],
      });
    });
    bundleSwitchUpdatesByVariantToken.set(nextVariantToken, {
      variantToken: nextVariantToken,
      quantity: 1,
      addOnIds: [],
    });

    setDisabled(true);
    const updatePromise = selectedBundleCartItem
      ? cartClient.updateQuantities(
          [...bundleSwitchUpdatesByVariantToken.values()],
          'overwrite',
        )
      : cartClient.updateQuantity(nextVariantToken, 1, [], 'add');

    return updatePromise
      .then(orderRefresh)
      .then(() => {
        trackBundleCdpEvent({
          eventName: CDP_EVENT_NAMES.SAAS_ON_SHOP.BUNDLE_PLAN_SELECTED,
          trackingData,
          offer: orderData?.hardware_bundle_offer,
          previousTier: selectedBundleTier,
          selectedTier: tierKey,
          variantToken: nextVariantToken,
          source: BUNDLE_CDP_EVENT_SOURCES.COMPARE_PLANS_MODAL,
        });

        onCartPageClick({
          actionItem: 'select_bundle_offer_tier',
          additionalParameters: {
            ...trackingData,
            operation,
            previousTier: selectedBundleTier,
            recommendedTier: orderData?.hardware_bundle_offer?.recommended_tier,
            selectedTier: tierKey,
            variantToken: nextVariantToken,
          },
        });
      })
      .finally(() => setDisabled(false));
  };

  const handleBundleRemove = () => {
    const removableBundleItems = bundleCartItems
      .map((item) => ({
        quantity: item.quantity,
        variantToken: item.product_variant?.token,
      }))
      .filter(({ quantity, variantToken }) => quantity && variantToken);

    if (removableBundleItems.length === 0) return Promise.resolve();
    setDisabled(true);
    return removableBundleItems
      .reduce(
        (promise, { quantity, variantToken }) =>
          promise.then(() =>
            cartClient.updateQuantity(variantToken, quantity, [], 'subtract'),
          ),
        Promise.resolve(),
      )
      .then(orderRefresh)
      .then(() => {
        trackBundleCdpEvent({
          eventName: CDP_EVENT_NAMES.SAAS_ON_SHOP.BUNDLE_PLAN_REMOVED,
          trackingData,
          offer: orderData?.hardware_bundle_offer,
          removedTier: selectedBundleTier,
          variantToken: removableBundleItems[0].variantToken,
          source: BUNDLE_CDP_EVENT_SOURCES.CART_CARD,
        });

        onCartPageClick({
          actionItem: 'remove_bundle_offer',
          additionalParameters: {
            ...trackingData,
            selectedTier: selectedBundleTier,
            variantToken: removableBundleItems[0].variantToken,
          },
        });
      })
      .finally(() => setDisabled(false));
  };

  // Bundle render predicate: payload present, not suppressed, not the
  // ineligible-to-migrate special case, and a recommended tier is available.
  // Bundle UI only lives in the SOFTWARE section.
  const hardwareBundleOffer = orderData?.hardware_bundle_offer;
  const hasHardwareBundleOffer =
    hardwareBundleOffer !== null && hardwareBundleOffer !== undefined;
  const hasRecommendedBundleTier =
    hardwareBundleOffer?.recommended_tier !== null &&
    hardwareBundleOffer?.recommended_tier !== undefined;
  const hasSelectedBundleTier = selectedBundleTier !== undefined;
  const shouldRenderBundleCard =
    sectionType === SECTION_TYPES.SOFTWARE &&
    hasHardwareBundleOffer &&
    hardwareBundleOffer.resolution !== BUNDLE_OFFER_RESOLUTIONS.SUPPRESS &&
    hardwareBundleOffer.resolution !==
      BUNDLE_OFFER_RESOLUTIONS.INELIGIBLE_TO_MIGRATE &&
    (hasRecommendedBundleTier || hasSelectedBundleTier);

  useEffect(() => {
    if (!shouldRenderBundleCard || bundleOfferedTrackedRef.current) return;

    bundleOfferedTrackedRef.current = true;
    trackBundleCdpEvent({
      eventName: CDP_EVENT_NAMES.SAAS_ON_SHOP.BUNDLE_OFFERED,
      trackingData,
      offer: hardwareBundleOffer,
      currentTier: selectedBundleTier,
      source: BUNDLE_CDP_EVENT_SOURCES.CART_CARD,
    });
  }, [
    hardwareBundleOffer,
    selectedBundleTier,
    shouldRenderBundleCard,
    trackingData,
  ]);

  const handleComparePlansOpen = () => {
    trackBundleCdpEvent({
      eventName: CDP_EVENT_NAMES.SAAS_ON_SHOP.BUNDLE_COMPARE_PLANS_OPENED,
      trackingData,
      offer: hardwareBundleOffer,
      currentTier: selectedBundleTier,
      source: BUNDLE_CDP_EVENT_SOURCES.CART_CARD,
    });
  };

  // Filter the auto-added `sw-square-{tier}` line item out of the default
  // <ActionableItem> loop so the bundle has exactly one rendered surface (the
  // <BundleOfferCard> below). Apply only in the SOFTWARE section.
  const visibleCartSection =
    sectionType === SECTION_TYPES.SOFTWARE
      ? cartSection.filter(
          (item) =>
            !BUNDLE_SQUARE_BASE_PRODUCT_KEYS.includes(item.base_product_key),
        )
      : cartSection;

  const cartItems = visibleCartSection.reduce((acc, cartItem, index) => {
    acc.push(
      <ActionableItem
        key={`item-${cartItem.id}`}
        category={cartItem.category}
        token={cartItem.product_variant.token}
        img={cartItem.img}
        slug={cartItem.product_variant.slug}
        hasPdp={cartItem.has_pdp}
        qty={cartItem.quantity}
        limit={cartItem.max_quantity}
        title={cartItem.product_variant.title}
        price={cartItem.price}
        currency={cartItem.currency}
        outOfStock={cartItem.out_of_stock}
        disclaimer={cartItem.disclaimer}
        shipQuote={shipQuotes?.find((quote) => quote.id === cartItem.id)}
        parts={cartItem.parts}
        plan={cartItem.plan}
        sharedLocationPlan={
          cartSection.find(
            (item) =>
              item.base_product_key === SOFTWARE_BASE_PRODUCT_KEYS.SW_KIOSK,
          )?.plan
        }
        softwareGroup={cartItem.software_group}
        onQtyChange={handleQtyChange}
        disabled={disabled}
        isSoftware={cartItem.is_software}
        isPnpSubscribed={isPnpSubscribed}
        locationCount={locationCount}
        kioskDeviceCount={kioskDeviceCount}
        k1aDeviceCount={k1aDeviceCount}
        baseProductKey={cartItem.base_product_key}
        bopisEligible={bopisEligible}
        isReadOnly={isReadOnly}
      />,
    );

    if (index !== visibleCartSection.length - 1) {
      acc.push(<Divider key={`hr-${cartItem.id}`} sx={{ my: 1 }} />);
    }

    return acc;
  }, []);

  const softwareBanners = fetchSoftwareSummaries(orderData).map((summary) => {
    if (!summary) return null;
    const { baseProductKey, softwareName, subscriptionState } = summary;

    // Kiosk P&P subscribers still pay per-device, so suppress subscription
    // banners (active, superset, and free-trial) that would confuse or block.
    if (isPnpSubscribed && isKioskSoftware(baseProductKey)) {
      return null;
    }

    const config = CART_BANNER_CONFIGS[subscriptionState];

    return (
      <CartBanner
        key={softwareName}
        config={config}
        softwareName={softwareName}
      />
    );
  });

  // Short-circuit only when there's nothing to show: no visible line items,
  // no empty-state component, AND no bundle card to render.
  if (
    visibleCartSection.length === 0 &&
    !emptyComponent &&
    !shouldRenderBundleCard
  ) {
    return null;
  }

  return (
    <div>
      <Grid container justifyContent="space-between" alignItems="flex-end">
        <h3>{sectionTitle}</h3>
        {!isReadOnly && titleButton}
      </Grid>
      {sectionType === SECTION_TYPES.SOFTWARE && softwareBanners}
      <Box
        sx={{
          border: 1,
          borderColor: 'grey.300',
          borderRadius: 1.5,
          boxSizing: 'border-box',
          marginTop: 2,
        }}
      >
        {visibleCartSection.length === 0 && emptyComponent
          ? emptyComponent
          : cartItems}
        {shouldRenderBundleCard && (
          <>
            {visibleCartSection.length > 0 && <Divider sx={{ my: 1 }} />}
            <BundleOfferCard
              offer={hardwareBundleOffer}
              selectedTier={selectedBundleTier}
              onAdd={handleBundleAdd}
              onComparePlansOpen={handleComparePlansOpen}
              onRemove={handleBundleRemove}
              onSelectTier={handleBundleSelect}
              disabled={disabled}
            />
          </>
        )}
      </Box>
    </div>
  );
};

export default ActionableCartSection;
